import objectHelper from "@digits-shared/helpers/objectHelper"
import { redactUrl } from "@digits-shared/helpers/redactUrl"
// Using alias to avoid ad blocker filters blocking "trackjs" in dev mode
import { TrackJS } from "@trackjs"
import { v4 as generateUUID } from "uuid"
import { installCSPViolationReporting } from "@digits-shared/initializers/cspViolationReporting"

const versionElement = document.querySelector("meta[name=version]") as HTMLMetaElement

/**
 * Setup for the Tracker script before it has loaded
 */

// Only enable in production (not including staging). Need to ensure staging flag
// is not true because staging runs as node env production to build libraries in prod.
const enabled = process.env.NODE_ENV === "production" && process.env.STAGING !== "true"

// Do not track non-production network errors
const networkEnabled = enabled && process.env.GRAPHQL_ENDPOINT?.split("/").pop() === "graphql"

class ErrorWithMetadata extends Error {
  constructor(
    message: string,
    readonly metadata: Record<string, string>
  ) {
    super(message)
  }
}

if (enabled) {
  const installed = TrackJS.install({
    enabled,
    network: {
      enabled: networkEnabled,
      error: networkEnabled,
    },
    token: "55a37693e013428bbebfc231b466cf5a",
    sessionId: generateUUID(),
    version: (versionElement && versionElement.content) || Date.now().toString(),
    onError: (payload, error) => {
      payload.url = redactUrl(payload.url)
      if (error instanceof ErrorWithMetadata) {
        Object.entries(error.metadata).forEach(([key, value]) =>
          payload.metadata.push({ key, value })
        )
      }
      return true
    },
  })
  if (installed) {
    installCSPViolationReporting((message, metadata) =>
      TrackJS.track(new ErrorWithMetadata(message, metadata))
    )
  }
  TrackJS.addMetadata("client-version", process.env.CURRENT_VERSION || "")
  window.TrackJS = TrackJS
} else {
  const prefix = process.env.NODE_ENV === "development" ? "[DEV]" : "[STAGING]"
  // In development or staging, disable event transmission and log errors to the console instead
  window.TrackJS = {
    version: "dev-mock",
    watch: () => () => null,
    watchAll: () => () => null,
    isInstalled: () => false,
    addMetadata: () => null,
    removeMetadata: () => null,
    attempt: () => null,
    install: () => true,
    configure: () => true,
    hash: "hash",
    console: objectHelper.keysOf(console).reduce(
      (devConsole, method) => {
        const original = console[method]
        if (typeof original === "function") {
          // oxlint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error
          devConsole[method] = original.bind(console, prefix)
        } else {
          devConsole[method] = original
        }
        return devConsole
      },
      {} as typeof console
    ),
    track: (error: Error) => {
      console.error(prefix, error)
    },
  }
}
